import { z } from 'zod';

/**
 * Cross-page continuity findings persisted on the order row (issue #823).
 *
 * The runner's post-page-gen / pre-PDF continuity sweep judges the
 * finished book in overlapping page windows plus one cover-pair call
 * (`reviewContinuity` skill), self-heals what a single targeted page
 * regen can fix, and then ships REGARDLESS — continuity is never a new
 * customer-facing failure state. What survives the self-heal is
 * recorded here so admin tooling / support can see what the judge still
 * flagged on a shipped book.
 *
 * `flag` is deliberately a permissive `z.string()`, never a `z.enum`:
 * the documented slugs (`wardrobe-drift`, `prop-drift`,
 * `background-drift`, `character-drift`, `duplicate-character`,
 * `text-image-mismatch`, `other` — the `CONTINUITY_REVIEW_FLAGS` subset of
 * the shared `ReviewFlag` union in `./reviewFlags.ts`) are prompt guidance,
 * and the server must stay free to introduce new ones without breaking any
 * consumer (CLAUDE.md permissive wire-schema rule). Stored rows carry
 * `prop-drift` verbatim and must keep doing so — that is exactly why the
 * union keeps BOTH it and its `object-drift` twin.
 *
 * `sweptAt` is an ISO-8601 timestamp. `findings` are overwritten on every
 * sweep (a "Try Again" re-finalize re-judges possibly-regenerated pages —
 * last write wins, intentional). `selfHealed` is NOT: it is the order's
 * durable self-heal ledger, read before each sweep to enforce the
 * 1-per-page / 15-per-book caps over the LIFETIME of the order and
 * accumulated on write, so a finalize retry can never refund the budget.
 * (The per-book cap was 6 until the #845 consistency overhaul raised it;
 * the number lives in `CONTINUITY_MAX_REGENS_PER_BOOK` on the server and
 * this prose must follow it.) `budgetEpoch` (#826) is the ONLY sanctioned
 * way to hand an order a fresh budget — it never deletes ledger entries.
 */
export const continuityFindingRecordSchema = z.object({
  /** Documented continuity slug, or any newer server-introduced slug. */
  flag: z.string(),
  /** Pages the finding involves. Exactly one id = self-heal eligible. */
  pageIds: z.array(z.string()),
  /** One concrete sentence naming the page, the thing, and the change. */
  note: z.string(),
  /**
   * The canonical object/character this finding is ABOUT — an object id from
   * the story canon, or the name the judge used.
   *
   * This is the cross-window MERGE KEY. `findingKey()` is `flag | pageIds |
   * note` today, so the same defect reported by two overlapping judge windows
   * produces two different keys (different page lists, differently-worded
   * notes) and is counted, budgeted and shipped as two defects. `(flag,
   * subject)` is stable across windows and wordings.
   *
   * Permissive `z.string()`: the judge names the subject in its own words and
   * the server will keep extending the canon vocabulary. The judge is HANDED
   * the canonical label list (`ReviewContinuityInput.canonicalSubjects`,
   * built from `csObjectIds` + the object inventory + the character map) and
   * told to pick from it; anything it invents anyway is normalised to a
   * slug (`normalizeSubjectSlug`) so at least two differently-punctuated
   * spellings of the same invention still merge. A fallback subject merges
   * LESS RELIABLY than a canonical one — two windows can word the same
   * defect differently enough to slug differently.
   *
   * OPTIONAL on purpose — `orders.continuity_findings` is untyped JSONB and
   * every row written before this field existed must keep parsing. Absent
   * means "the judge did not name a subject", which is a real outcome, not an
   * error.
   */
  subject: z.string().optional(),
  /**
   * The single page that actually disagrees with canon, when the finding
   * spans several.
   *
   * A multi-page finding is not repairable without this: the repair is a
   * targeted page regen, and picking `pageIds[0]` is a guess that can repaint
   * the CORRECT page and leave the defect. Naming the culprit is what makes a
   * multi-page finding self-heal eligible at all.
   *
   * Expected to be one of {@link pageIds} when present, but that is NOT
   * enforced here — a stored row whose culprit drifted out of the page list is
   * still a readable audit record, and the sweep validates membership at the
   * point where it would actually spend budget.
   *
   * OPTIONAL for the same backward-compatibility reason as {@link subject}.
   */
  culpritPageId: z.string().optional(),
});

export type ContinuityFindingRecord = z.infer<
  typeof continuityFindingRecordSchema
>;

/**
 * The largest budget epoch that can ever be PERSISTED (issue #826, review
 * round 5) — `2^31 - 1`, the Postgres `int4` maximum.
 *
 * `orders.continuity_findings` is untyped JSONB, so nothing in the column
 * enforces this. The bound comes from the WRITE: `persistContinuityFindings`
 * computes the epoch in SQL (`GREATEST(stored, mine)`) so it can be
 * monotonic under concurrency, and that arithmetic runs in `int4`. Both
 * sides of that `GREATEST` are therefore clamped to this value — otherwise
 * a larger epoch raises `integer out of range`, the whole `UPDATE` aborts,
 * and the function's best-effort catch silently loses the findings AND the
 * new spend-ledger entries. Losing ledger entries is the one thing the
 * durable ledger exists to prevent.
 *
 * The epoch only ever increments by ONE per paid customer regen, so this
 * ceiling is unreachable in practice; it exists so the failure mode of an
 * absurd value is "clamp" rather than "silently drop the write".
 *
 * Enforced in THREE places, deliberately: here at the schema (so a stored
 * value we could not have written is rejected as an untrustworthy ledger
 * rather than silently re-interpreted), in TypeScript where the epoch is
 * incremented (so the in-memory epoch and the `epoch` stamped on each new
 * `selfHealed` entry can never disagree), and in the SQL itself (so the
 * `UPDATE` is total for any caller).
 */
export const CONTINUITY_BUDGET_EPOCH_MAX = 2147483647;

/** One page the sweep actually regenerated, and why. */
export const continuitySelfHealRecordSchema = z.object({
  pageId: z.string(),
  flag: z.string(),
  /**
   * WHICH character/object this repair was charged for — the `subject` of the
   * {@link ContinuityFindingRecord} that paid for it.
   *
   * `flag` alone cannot tell two defects on one page apart: a page can drift
   * `wardrobe-drift` on the child AND `wardrobe-drift` on the grandmother, and
   * a ledger that records only the flag reads those as the same spend. Pairing
   * the flag with the subject is what lets the sweep decide whether a NEW
   * finding on an already-repaired page is a genuinely different defect (worth
   * a second, conditional repair) or the same one coming back (worth nothing —
   * the repair did not converge).
   *
   * Permissive `z.string()` for the same reason as
   * {@link ContinuityFindingRecord.subject}: it is drawn from the canonical
   * subject vocabulary when the judge picks one, and from a normalised slug of
   * whatever it invented when it does not, and that vocabulary keeps growing.
   *
   * OPTIONAL on purpose — `orders.continuity_findings` is untyped JSONB and
   * every entry written before this field existed must keep parsing. **Absent
   * means "unknown subject", NEVER "no subject".** The two read very
   * differently: "no subject" would be a fact about the defect, while an
   * absent field is an absence of evidence about a repair we really did charge
   * for.
   *
   * 🚨 A record with an absent `subject` can therefore never satisfy a
   * "different `(flag, subject)`" test: an unknown subject must read as
   * "possibly the SAME subject", which blocks a second repair. That is the
   * conservative direction — the cost of being wrong is a defect that ships
   * (which this sweep already ships by design), not an unbounded re-spend on
   * the customer's artwork, which is the one thing the durable ledger exists
   * to prevent.
   */
  subject: z.string().optional(),
  /**
   * Which BUDGET EPOCH spent this regen (issue #826).
   *
   * The ledger is append-only and is never pruned — it is the order's
   * audit trail. A budget RESET (a customer-initiated regeneration
   * produces artwork that has never been continuity-judged) therefore
   * cannot delete entries; it bumps
   * {@link ContinuityFindingsRecord.budgetEpoch} instead, and the sweep
   * counts only entries whose epoch matches the current one.
   *
   * OPTIONAL on purpose: every row written before #826 has no epoch, and
   * those rows must keep parsing. **Absent means epoch 0.**
   *
   * Bounded by {@link CONTINUITY_BUDGET_EPOCH_MAX}: the writer can only
   * ever stamp an entry with a clamped epoch, so a larger one is not a
   * value this system produced.
   */
  epoch: z
    .number()
    .int()
    .nonnegative()
    .max(CONTINUITY_BUDGET_EPOCH_MAX)
    .optional(),
  /**
   * Did this repair actually remove the defect it was charged for?
   *
   * A QA sweep found 3 of 6 observed repairs left the exact defect present and
   * were still charged against the lifetime budget. Recording the verified
   * outcome is what turns that from an invisible loss into a measurable one.
   *
   * THREE-VALUED on purpose — `null` is NOT `false`:
   *
   * - `true`  — the post-repair re-judge no longer reports the defect.
   * - `false` — the re-judge still reports it. The spend was wasted.
   * - `null`  — INDETERMINATE. We have no evidence either way: the re-judge
   *   did not run, it could not see the region, or it returned
   *   `cannot-tell`. This is a real and distinct outcome from `false`, and
   *   collapsing the two would inflate the observed failure rate and mislead
   *   any future decision about repair budgets.
   * - absent  — every entry written before this field existed. Read it as
   *   `null` (unknown), never as `false`.
   *
   * OPTIONAL for the same reason as {@link epoch}: the column is untyped JSONB
   * and pre-existing ledger entries must keep parsing.
   */
  repairEffective: z.boolean().nullable().optional(),
});

export type ContinuitySelfHealRecord = z.infer<
  typeof continuitySelfHealRecordSchema
>;

export const continuityFindingsRecordSchema = z.object({
  /** ISO-8601 timestamp of the sweep that produced this record. */
  sweptAt: z.string(),
  /** RESIDUAL findings — what remained after the bounded self-heal. */
  findings: z.array(continuityFindingRecordSchema),
  /**
   * DURABLE ledger of every page the sweep has regenerated for this
   * order, across ALL sweeps AND all budget epochs — accumulated, never
   * overwritten and never pruned. The lifetime self-heal budget is
   * computed from the subset whose `epoch` matches {@link budgetEpoch}.
   */
  selfHealed: z.array(continuitySelfHealRecordSchema),
  /**
   * The order's CURRENT self-heal budget epoch (issue #826). Absent = 0.
   *
   * Bumping this is how a budget reset is expressed without destroying
   * history: `selfHealed` entries stamped with an older epoch stay in the
   * ledger but no longer consume the current budget.
   *
   * OPTIONAL on purpose — pre-#826 rows have no such field and must keep
   * parsing (no migration is needed; the column is JSONB).
   *
   * Bounded by {@link CONTINUITY_BUDGET_EPOCH_MAX} (review round 5). The
   * bound is deliberately a PARSE failure rather than a silent clamp: a
   * stored epoch outside the range the write path can produce means the
   * column holds something this system did not write, and the sweep
   * already has exactly the right response to a ledger it cannot trust —
   * judge, spend nothing, write nothing. Silently clamping it instead
   * would strand every `selfHealed` entry stamped with the larger epoch,
   * which reads as "no spend in the current epoch" and hands the order a
   * full fresh budget — spend laundering, the failure mode this whole
   * ledger exists to prevent.
   */
  budgetEpoch: z
    .number()
    .int()
    .nonnegative()
    .max(CONTINUITY_BUDGET_EPOCH_MAX)
    .optional(),
});

export type ContinuityFindingsRecord = z.infer<
  typeof continuityFindingsRecordSchema
>;

/**
 * The JUDGMENT half of a {@link ContinuityFindingsRecord} — what the sweep
 * said ABOUT A PARTICULAR SET OF ARTWORK, with the durable spend ledger
 * deliberately left out (issue #826, review round 2).
 *
 * The two halves have OPPOSITE rollback semantics, which is the whole
 * reason this type exists:
 *
 * - `sweptAt` + `findings` describe images. If those images are rolled
 *   back — a paid customer regen whose PDF assembly then failed, restored
 *   by `handleRegenerationFailure` — the judgment describes artwork that
 *   no longer exists and must be rolled back WITH it.
 * - `selfHealed` + `budgetEpoch` describe MONEY that was really spent and
 *   must survive any rollback, or a repeatedly-failing regen could refund
 *   itself an unbounded number of image generations. `budgetEpoch` is
 *   additionally monotonic in SQL (`GREATEST(...)`).
 *
 * So a regen kickoff snapshots exactly this much onto
 * `RegenInFlight.priorContinuityFindings`, and the rollback restores
 * exactly this much — never the ledger.
 */
export const continuityJudgmentSnapshotSchema = z.object({
  /** ISO-8601 timestamp of the sweep that produced {@link findings}. */
  sweptAt: z.string(),
  /** RESIDUAL findings for the artwork that was current at snapshot time. */
  findings: z.array(continuityFindingRecordSchema),
});

export type ContinuityJudgmentSnapshot = z.infer<
  typeof continuityJudgmentSnapshotSchema
>;
